import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useCoinPriceAPIStore } from './api/coinPrice'
import { useAccountQuery } from '@/composables'
import { useInfoAPIStore, useUserAPIStore, useWalletStore } from '@/stores'
import { type Locale } from '@/types'

export const useLocaleStore = defineStore('locale', () => {
  const localeInfo = ref<Locale>()
  const coinPriceAPIStore = useCoinPriceAPIStore()
  const walletStore = useWalletStore()
  const infoAPIStore = useInfoAPIStore()
  const userAPIStore = useUserAPIStore()
  const { account } = useAccountQuery()

  async function getLocaleInfo() {
    if (typeof localeInfo.value === 'undefined') {
      const locale = infoAPIStore.localeList?.find(
        (locale) => locale.id === userAPIStore.userInfo?.localeId,
      )

      localeInfo.value = {
        locale: convertLocaleCode(locale?.localeGroup as string),
        currency: infoAPIStore.currentCurrency,
        unit: getCurrencyUnit(infoAPIStore.currentCurrency),
      }
    }
  }

  async function setLocaleInfo(localeGroup: string, currency: string) {
    if (localeInfo.value) {
      localeInfo.value = {
        ...localeInfo.value,
        locale: convertLocaleCode(localeGroup),
        currency: getCurrency(currency),
        unit: getCurrencyUnit(currency),
      }
      coinPriceAPIStore.coinPriceList = []
      coinPriceAPIStore.chart7d = []
      await coinPriceAPIStore.fetchCoinPrice({
        force: true,
        coinGeckoIds: walletStore.accountList
          .map((account) => account.coin?.coinGeckoId)
          .filter((id) => id)
          .concat(
            walletStore.accountTokenList
              .map((accountToken) => accountToken.coin?.coinGeckoId)
              .filter((id) => id),
          ) as string[],
        currency: localeInfo.value.currency,
      })
      if (account.value) {
        await coinPriceAPIStore.fetchChart7d({
          force: true,
          coinGeckoId: account.value.coin?.coinGeckoId!,
          currency: localeInfo.value?.currency!,
        })
      }
    }
  }

  function convertLocaleCode(country: string): string {
    switch (country) {
      case 'US':
      case 'CA':
      case 'GB':
      case 'IE':
      case 'AU':
      case 'NZ':
      case 'MX':
      case 'IL':
      case 'JP':
      case 'TH':
      case 'CN':
      case 'HK':
      case 'TW':
        return 'en-US' // 1,234,567
      case 'AR':
      case 'CL':
      case 'CO':
      case 'ES':
      case 'DK':
      case 'DE':
      case 'GR':
      case 'ID':
      case 'IT':
      case 'NL':
      case 'BR':
      case 'TR':
        return 'es-ES' // 1.234.567
      case 'FI':
      case 'BE':
      case 'CH':
      case 'FR':
      case 'HU':
      case 'CZ':
      case 'AT':
      case 'ZA':
      case 'NO':
      case 'PL':
      case 'PT':
      case 'RU':
      case 'SK':
      case 'SE':
        return 'fi-FR' // 1 234 567
      case 'IN':
      case 'LK':
        return 'hi-IN' // 12,34,567
      default:
        return 'en-US'
    }
  }

  function getCurrency(currency: string) {
    switch (currency) {
      case 'USD':
      case 'EUR':
      case 'RUB':
      case 'IDR':
      case 'KRW':
      case 'CNY':
      case 'TWD':
      case 'JPY':
        return currency.toLowerCase()
      default:
        return 'usd'
    }
  }

  function getCurrencyUnit(currency: string) {
    switch (currency) {
      case 'USD':
        return '$'
      case 'EUR':
        return '€'
      case 'RUB':
        return '₽'
      case 'IDR':
        return '₹'
      case 'KRW':
        return '₩'
      case 'CNY':
        return '¥'
      case 'TWD':
        return '$'
      case 'JPY':
        return '¥'
      default:
        return '$'
    }
  }

  function applyDigitRule(
    digits: number | string,
    options?: Intl.NumberFormatOptions,
  ): string {
    const locale = localeInfo.value?.locale
    let roundingMode: string = ''

    if (options?.roundingMode) {
      roundingMode = options?.roundingMode
    } else {
      roundingMode = 'floor'
    }
    if (!digits && digits !== 0) return ''
    return digits.toLocaleString(locale, {
      ...options,
      //@ts-expect-error
      roundingMode: roundingMode,
    })
  }

  function applyLocaleCurrencyDigitRule(digits: number | string) {
    if (!localeInfo.value) return
    if (typeof digits === 'string') {
      digits = Number(digits)
    }
    const localeNumber = new Intl.NumberFormat(localeInfo.value?.locale, {
      style: 'currency',
      currency: localeInfo.value?.currency,
    }).format(digits)
    const regex = /[^\d.,]/g
    if (regex.test(localeNumber[0])) {
      return localeNumber.slice(1).trim()
    }
    if (regex.test(localeNumber[localeNumber.length - 1])) {
      return localeNumber.slice(0, localeNumber.length - 1).trim()
    }
    return localeNumber
  }

  function formatApplyContractionRule(digits: number) {
    if (digits < 1000) {
      return digits
    } else if (digits >= 1000 && digits < 1_000_000) {
      return (digits / 1000).toFixed(1) + 'K'
    } else if (digits >= 1_000_000 && digits < 1_000_000_000) {
      return (digits / 1_000_000).toFixed(1) + 'M'
    } else if (digits >= 1_000_000_000 && digits < 1_000_000_000_000) {
      return (digits / 1_000_000_000).toFixed(1) + 'B'
    } else if (digits >= 1_000_000_000_000 && digits < 1_000_000_000_000_000) {
      return (digits / 1_000_000_000_000).toFixed(1) + 'T'
    }
  }

  const reset = () => {
    localeInfo.value = undefined
  }
  return {
    localeInfo,
    getLocaleInfo,
    applyDigitRule,
    applyLocaleCurrencyDigitRule,
    formatApplyContractionRule,
    setLocaleInfo,
    reset,
  }
})
